/**
 * Canonical clause tag keys and parsing for the admin UI — the frontend mirror
 * of backend/src/utils/clauseTagKeys.ts, bound to it (and to the Python
 * extractor) by tests/shared/clauseTagFixtures/clauseTagKeys.json.
 *
 * Clause and option IDs may contain underscores (management_committee,
 * not_yet), so a tag CANNOT be parsed by splitting on a delimiter — the option
 * boundary is genuinely ambiguous. Resolution matches whole generated keys and
 * reports ambiguity (two records deriving one key) rather than guessing, so the
 * clause manager never invents a clause ID a tag does not unambiguously name.
 */

export type ClauseTagFamily = 'clause' | 'check' | 'tick' | 'unanswered';
export type ClauseTagForm = 'plain' | 'open' | 'close' | 'inverted';

export interface ClauseTagRef {
  id: string;
  options: { id: string }[];
}

export interface ResolvedClauseTag {
  kind: 'resolved';
  family: ClauseTagFamily;
  form: ClauseTagForm;
  clauseId: string;
  optionId?: string;
}

export interface AmbiguousClauseTag {
  kind: 'ambiguous';
  candidates: { clauseId: string; optionId: string }[];
}

export type ClauseTagResolution = ResolvedClauseTag | AmbiguousClauseTag;

/** Which forms each family may legally take. */
export const ALLOWED_FORMS: Record<ClauseTagFamily, ClauseTagForm[]> = {
  clause: ['open', 'close'],
  unanswered: ['open', 'close'],
  check: ['plain'],
  tick: ['open', 'close', 'inverted'],
};

const OPTION_FAMILIES = ['clause', 'check', 'tick'] as const;
const ALL_FAMILIES: ClauseTagFamily[] = ['clause', 'check', 'tick', 'unanswered'];

export function clauseTagKey(clauseId: string, optionId: string): string {
  return `clause_${clauseId}_${optionId}`;
}
export function checkTagKey(clauseId: string, optionId: string): string {
  return `check_${clauseId}_${optionId}`;
}
export function tickTagKey(clauseId: string, optionId: string): string {
  return `tick_${clauseId}_${optionId}`;
}
export function unansweredTagKey(clauseId: string): string {
  return `unanswered_${clauseId}`;
}

/** Split a tag into its form (from the leading delimiter) and its bare key. */
export function readTagForm(tag: string): { form: ClauseTagForm; key: string } {
  const bare = tag.replace(/^\{/, '').replace(/\}$/, '');
  if (bare.startsWith('#')) return { form: 'open', key: bare.slice(1) };
  if (bare.startsWith('/')) return { form: 'close', key: bare.slice(1) };
  if (bare.startsWith('^')) return { form: 'inverted', key: bare.slice(1) };
  return { form: 'plain', key: bare };
}

/** The clause-tag family a bare (form-stripped) key belongs to, or null. */
export function clauseTagFamily(key: string): ClauseTagFamily | null {
  for (const family of ALL_FAMILIES) {
    if (key.startsWith(`${family}_`)) return family;
  }
  return null;
}

/** Resolve a template tag, reporting ambiguity rather than guessing. */
export function resolveClauseTag(
  tag: string,
  clauses: ClauseTagRef[]
): ClauseTagResolution | null {
  const { form, key } = readTagForm(tag);

  if (key.startsWith('unanswered_')) {
    const clauseId = key.slice('unanswered_'.length);
    return clauses.some((c) => c.id === clauseId)
      ? { kind: 'resolved', family: 'unanswered', form, clauseId }
      : null;
  }

  for (const family of OPTION_FAMILIES) {
    const prefix = `${family}_`;
    if (!key.startsWith(prefix)) continue;

    const candidates: { clauseId: string; optionId: string }[] = [];
    for (const clause of clauses) {
      for (const option of clause.options) {
        if (key === `${prefix}${clause.id}_${option.id}`) {
          candidates.push({ clauseId: clause.id, optionId: option.id });
        }
      }
    }

    if (candidates.length === 0) return null;
    if (candidates.length > 1) return { kind: 'ambiguous', candidates };

    return {
      kind: 'resolved',
      family,
      form,
      clauseId: candidates[0].clauseId,
      optionId: candidates[0].optionId,
    };
  }

  return null;
}

/** Every derived key produced by more than one clause/option pair. */
export function findDerivedKeyCollisions(
  clauses: ClauseTagRef[]
): { key: string; candidates: { clauseId: string; optionId: string }[] }[] {
  const byKey = new Map<string, { clauseId: string; optionId: string }[]>();

  for (const clause of clauses) {
    for (const option of clause.options) {
      const key = clauseTagKey(clause.id, option.id);
      const list = byKey.get(key) ?? [];
      list.push({ clauseId: clause.id, optionId: option.id });
      byKey.set(key, list);
    }
  }

  return [...byKey.entries()]
    .filter(([, candidates]) => candidates.length > 1)
    .map(([key, candidates]) => ({ key, candidates }));
}

export interface ClauseTagParseResult {
  /** IDs of clauses that at least one clause-family tag resolves to. */
  referencedClauseIds: string[];
  /** Clause-family tags (bare keys) that resolve to no clause+option. */
  unknownTags: string[];
  /** Clause-family tags that resolve to more than one record, with candidates. */
  ambiguousTags: { tag: string; candidates: { clauseId: string; optionId: string }[] }[];
  /** Bare keys of the tags referencing each clause (sorted, deduped) — what a
   *  rename/delete of that clause would orphan. */
  referencingTagsByClauseId: Record<string, string[]>;
  /** Option ids referenced per clause (unanswered_ tags contribute none) —
   *  what removing that option would orphan. */
  referencedOptionIdsByClauseId: Record<string, string[]>;
}

/**
 * Classify a template's body tags against the clause set. Only clause-family
 * tags (clause_/check_/tick_/unanswered_) are considered — module_, schedule_,
 * and ordinary placeholders are ignored entirely, never reported as unknown. An
 * unresolved clause-family tag is reported verbatim (as its bare key), and an
 * ambiguous one separately with its candidates, so the UI never guesses a
 * clause ID a delimiter split would get wrong.
 */
export function parseClauseTags(
  bodyTags: string[],
  clauses: ClauseTagRef[]
): ClauseTagParseResult {
  const referencedClauseIds = new Set<string>();
  const unknownTags = new Set<string>();
  const ambiguous = new Map<string, { clauseId: string; optionId: string }[]>();
  const tagsByClause = new Map<string, Set<string>>();
  const optionsByClause = new Map<string, Set<string>>();

  for (const tag of bodyTags) {
    const { key } = readTagForm(tag);
    if (!clauseTagFamily(key)) continue; // not clause markup — ignore

    const resolution = resolveClauseTag(tag, clauses);
    if (resolution === null) {
      unknownTags.add(key);
    } else if (resolution.kind === 'ambiguous') {
      ambiguous.set(key, resolution.candidates);
    } else {
      referencedClauseIds.add(resolution.clauseId);
      if (!tagsByClause.has(resolution.clauseId)) {
        tagsByClause.set(resolution.clauseId, new Set());
      }
      tagsByClause.get(resolution.clauseId)!.add(key);
      if (resolution.optionId !== undefined) {
        if (!optionsByClause.has(resolution.clauseId)) {
          optionsByClause.set(resolution.clauseId, new Set());
        }
        optionsByClause.get(resolution.clauseId)!.add(resolution.optionId);
      }
    }
  }

  return {
    referencedClauseIds: [...referencedClauseIds],
    unknownTags: [...unknownTags],
    ambiguousTags: [...ambiguous.entries()].map(([tag, candidates]) => ({ tag, candidates })),
    referencingTagsByClauseId: Object.fromEntries(
      [...tagsByClause.entries()].map(([clauseId, tags]) => [clauseId, [...tags].sort()])
    ),
    referencedOptionIdsByClauseId: Object.fromEntries(
      [...optionsByClause.entries()].map(([clauseId, ids]) => [clauseId, [...ids].sort()])
    ),
  };
}

/** Existing clauses that no tag in the template references. */
export function findOrphanedClauseIds(
  referencedClauseIds: string[],
  existingClauseIds: string[]
): string[] {
  const referenced = new Set(referencedClauseIds);
  return existingClauseIds.filter((id) => !referenced.has(id));
}
